import React, { useEffect, useRef } from "react";
import styles from "./AppShell.module.css";
import { useUIStore } from "@store";
import AppSidebar from "./AppSidebar";
import { trackEvent } from "../../services/analyticsService";
import ChatSidebarView from "../chat/ChatSidebarView";
import DocumentsPage from "../pages/DocumentsPage";
import PromptsPage from "../pages/PromptsPage";
import MindspaceInfoPage from "../pages/MindspaceInfoPage";
import { useAuthStore, useChatStore, useMindspaceStore } from "@store";
import { initializeChatData } from "../../App";
import { logDebug, logError } from "../../utils/logging";
import ErrorMessageDisplay from "../common/ErrorMessageDisplay";
import ToastContainer from "../common/ToastContainer";

const AppShell: React.FC = () => {
  const route = useUIStore((s) => s.route);
  const { isSidebarOpenOnMobile, closeSidebarMobile, toggleSidebarMobile } =
    useUIStore();
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    trackEvent("pageview", { route });
  }, [route]);

  // Ensure initial auth data is loaded in fullpage mode (widget triggers this via ChatBubble)
  const isLoadingInitialData = useAuthStore((s) => s.isLoadingInitialData);
  const initialDataLoaded = useAuthStore((s) => s.initialDataLoaded);
  const jwtPresent = useAuthStore((s) => !!s.jwt);
  const setLoadingInitialData = useAuthStore((s) => s.setLoadingInitialData);
  const setInitialData = useAuthStore((s) => s.setInitialData);
  const setInitialDataError = useAuthStore((s) => s.setInitialDataError);
  const initialDataError = useAuthStore((s) => s.initialDataError);
  const apiError = useAuthStore((s) => s.apiError);
  const isRateLimited = useAuthStore((s) => s.isRateLimited);
  const clearApiError = useAuthStore((s) => s.clearApiError);

  // Only auto-initialize if explicitly in fullpage mode (prevent widget background freeze)
  const isFullpageMode =
    typeof window !== "undefined" &&
    !!document.getElementById("smartie-fullpage-root");

  useEffect(() => {
    // Prevent infinite loop: stop if already loaded, currently loading, or if a previous attempt failed
    // Also ensure we only auto-init if we are actually in fullpage mode or user has opened the chat
    if (
      isFullpageMode &&
      !initialDataLoaded &&
      !isLoadingInitialData &&
      !initialDataError
    ) {
      logDebug("AppShell: initializing auth data for fullpage mode...");
      initializeChatData(
        setLoadingInitialData,
        setInitialData,
        setInitialDataError,
      ).catch((err) =>
        logError("AppShell: failed to initialize auth data", err),
      );
    }
  }, [
    isFullpageMode,
    initialDataLoaded,
    isLoadingInitialData,
    initialDataError,
    setLoadingInitialData,
    setInitialData,
    setInitialDataError,
  ]);

  // Initialize mindspace (v2) when JWT becomes available
  const loadMindspaces = useMindspaceStore((s) => s.loadMindspaces);
  const isMindspaceLoading = useMindspaceStore((s) => s.isLoading);
  const isMindspaceInitialized = useMindspaceStore((s) => s.isInitialized);
  const mindspaceError = useMindspaceStore((s) => s.error);

  useEffect(() => {
    if (
      jwtPresent &&
      !isMindspaceInitialized &&
      !isMindspaceLoading &&
      !mindspaceError
    ) {
      logDebug("AppShell: JWT available, initializing mindspaces...");
      loadMindspaces().catch((err) =>
        logError("AppShell: mindspace init failed", err),
      );
    }
  }, [
    jwtPresent,
    isMindspaceInitialized,
    isMindspaceLoading,
    mindspaceError,
    loadMindspaces,
  ]);

  // Load chat history when initial data and session are ready
  const sessionId = useChatStore((s) => s.sessionId);
  const fetchAndLoadHistory = useChatStore((s) => s.fetchAndLoadHistory);
  useEffect(() => {
    if (jwtPresent && sessionId) {
      logDebug(`AppShell: loading history for session ${sessionId}...`);
      fetchAndLoadHistory();
    }
  }, [jwtPresent, sessionId, fetchAndLoadHistory]);

  useEffect(() => {
    if (apiError || isRateLimited) {
      const timer = setTimeout(() => {
        clearApiError();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [apiError, isRateLimited, clearApiError]);

  return (
    <div id="smartie-fullpage-root-shell" className={styles.shell}>
      {/* Mobile header toggle */}
      <div className={styles.mobileHeader}>
        <button
          className={styles.menuButton}
          aria-label="Deschide meniul"
          aria-expanded={isSidebarOpenOnMobile}
          onClick={toggleSidebarMobile}
        >
          ☰
        </button>
        <div className={styles.mobileBrand}>Smartie</div>
      </div>

      {/* Sidebar and overlay */}
      {isSidebarOpenOnMobile && (
        <div
          className={styles.backdrop}
          onClick={closeSidebarMobile}
          aria-hidden="true"
        />
      )}
      <nav
        className={
          isSidebarOpenOnMobile
            ? `${styles.sidebar} ${styles.sidebarOpen}`
            : styles.sidebar
        }
        aria-label="Navigare principală"
      >
        <div className={styles.sidebarHeaderMobileOnly}>
          <button
            ref={closeBtnRef}
            className={styles.closeSidebarBtn}
            onClick={closeSidebarMobile}
            aria-label="Închide meniul"
          >
            ✕
          </button>
        </div>
        <AppSidebar />
      </nav>
      <main className={styles.main} role="main">
        <ToastContainer />
        {isRateLimited && (
          <ErrorMessageDisplay
            message="Ați atins limita de mesaje pentru moment. Vă rugăm să încercați mai târziu."
            type="warning"
            className={styles.globalError}
          />
        )}
        {apiError && !isRateLimited && (
          <ErrorMessageDisplay
            message={`A apărut o eroare API: ${apiError}`}
            type="error"
            className={styles.globalError}
          />
        )}
        {isMindspaceLoading && !isRateLimited && !apiError && (
          <ErrorMessageDisplay
            message="Se inițializează contextul aplicației..."
            type="info"
            className={styles.globalError}
          />
        )}
        {mindspaceError && !isMindspaceLoading && !isRateLimited && !apiError && (
          <ErrorMessageDisplay
            message={`Eroare la inițializarea contextului: ${mindspaceError}`}
            type="error"
            className={styles.globalError}
            action={{
              label: "Reîncearcă",
              onClick: () => {
                logDebug(
                  "AppShell: User clicked retry for mindspace initialization",
                );
                loadMindspaces().catch((err) =>
                  logError("AppShell: mindspace init retry failed", err),
                );
              },
            }}
          />
        )}

        {route === "welcome" && <ChatSidebarView />}
        {route === "chat" && <ChatSidebarView />}
        {route === "documents" && <DocumentsPage />}
        {route === "prompts" && <PromptsPage />}
        {route === "mindspace" && <MindspaceInfoPage />}
      </main>
    </div>
  );
};

export default AppShell;
